package com.merkost.handpass.admin.pages

import androidx.compose.runtime.*
import com.merkost.handpass.admin.components.*
import com.merkost.handpass.admin.container.ListingsContainer
import com.merkost.handpass.admin.util.formatPriceCents
import com.merkost.handpass.admin.contract.*
import com.merkost.handpass.api.dto.admin.AdminListingListItem
import com.merkost.handpass.sharedweb.theme.*
import com.varabyte.kobweb.core.Page
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import com.merkost.handpass.admin.di.koinContainer
import pro.respawn.flowmvi.compose.dsl.subscribe

@Page("/listings")
@Composable
fun ListingsPage() {
    AdminPageScaffold(title = "Listing Management", currentPath = "/listings", showTitle = false) {
        val container = koinContainer<ListingsContainer>()
        with(container.store) {
            val state by subscribe { action ->
                when (action) {
                    is ListingsAction.Navigate -> navigate(action.path)
                }
            }
            ListingsContent(state, onIntent = ::intent)
        }
    }
}

@Composable
private fun AdminPageScope.ListingsContent(state: ListingsState, onIntent: (ListingsIntent) -> Unit) {
    AdminPageHeader(
        title = "Listings",
        subtitle = listingsSubtitle(state),
        actions = {
            AdminSearchInput(
                initial = state.search,
                onDebouncedChange = { onIntent(ListingsIntent.SetSearch(it)) },
                placeholder = "Search listing title…",
                widthPx = 280,
            )
            AdminButton(label = "Refresh", onClick = { onIntent(ListingsIntent.Refresh) }, variant = AdminButtonVariant.Primary, size = AdminButtonSize.SM)
        },
    )

    Div({
        style {
            display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(HandpassSpacing.SM)
            property("flex-wrap", "wrap"); property("margin-bottom", "${HandpassSpacing.XL}")
        }
    }) {
        LISTING_STATUS_FILTERS.forEach { filter ->
            AdminButton(
                label = filter,
                onClick = { onIntent(ListingsIntent.SetStatusFilter(filter)) },
                variant = if (state.statusFilter == filter) AdminButtonVariant.Primary else AdminButtonVariant.Secondary,
                size = AdminButtonSize.SM,
            )
        }
    }

    AdminListSection(
        title = "Listings",
        count = state.totalCount.toInt(),
        data = state.data,
        emptyTitle = "No listings found",
        skeletonRows = 8,
        onRetry = { onIntent(ListingsIntent.Refresh) },
    ) { rows ->
        ListingsTable(rows = rows, state = state, onIntent = onIntent)
    }

    Div({ style { property("margin-top", "${HandpassSpacing.XL}") } }) {
        AdminPagination(
            currentPage = state.page,
            totalPages = state.totalPages,
            totalCount = state.totalCount,
            onPageChange = { onIntent(ListingsIntent.GoToPage(it)) },
        )
    }
}

private fun listingsSubtitle(state: ListingsState): String {
    val count = state.totalCount
    val noun = if (count == 1L) "listing" else "listings"
    return "$count $noun"
}

@Composable
private fun ListingsTable(rows: List<AdminListingListItem>, state: ListingsState, onIntent: (ListingsIntent) -> Unit) {
    AdminTable(
        columns = listOf(
            AdminColumn<AdminListingListItem>("", "56px") {
                Div({
                    style {
                        width(48.px)
                        height(48.px)
                        borderRadius(HandpassRadius.XS)
                        property("overflow", "hidden")
                        property("background-color", SurfaceMuted.value().toString())
                    }
                }) {
                    if (it.imageUrls.isNotEmpty()) {
                        Img(it.imageUrls.first()) {
                            style { width(100.percent); height(100.percent); property("object-fit", "cover") }
                        }
                    }
                }
            },
            AdminColumn("Title", "2fr", sortKey = "title") {
                Span({
                    style {
                        fontWeight(600)
                        fontSize(HandpassTypography.Body)
                        color(OnSurface.value())
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("white-space", "nowrap")
                    }
                }) {
                    Text(it.title)
                }
            },
            AdminColumn("Seller", "1fr") {
                Span({ style { fontSize(HandpassTypography.Body); color(OnSurfaceMuted.value()) } }) {
                    Text(it.sellerName ?: "-")
                }
            },
            AdminColumn("Location", "1fr") {
                val place = listOfNotNull(it.city, it.state).filter { part -> part.isNotBlank() }.joinToString(", ")
                Span({ style { fontSize(HandpassTypography.Caption); color(OnSurfaceMuted.value()) } }) {
                    Text(place.ifBlank { "—" })
                }
            },
            AdminColumn("Status", "120px") {
                AccountStatusBadge(it.status)
            },
            AdminColumn("Price", "120px", sortKey = "price_cents") {
                Span({ style { fontSize(HandpassTypography.Body); color(OnSurface.value()) } }) {
                    Text(formatPriceCents(it.priceCents))
                }
            },
            AdminColumn("Created", "100px", sortKey = "created_at") {
                Span({ style { fontSize(HandpassTypography.Caption); color(OnSurfaceMuted.value()) } }) {
                    Text(it.createdAt.take(10))
                }
            },
            AdminColumn("Reports", "80px") {
                Span({
                    style {
                        fontSize(HandpassTypography.Body)
                        color(if (it.reportCount > 0) Danger.value() else OnSurface.value())
                    }
                }) {
                    Text(it.reportCount.toString())
                }
            },
        ),
        rows = rows,
        rowId = { it.id },
        onRowClick = { onIntent(ListingsIntent.TapListing(it.id)) },
        sortBy = state.sortBy,
        sortAsc = state.sortAsc,
        onSort = { key ->
            val newAsc = if (state.sortBy == key) !state.sortAsc else true
            onIntent(ListingsIntent.SetSort(key, newAsc))
        },
        flush = true,
    )
}
